import { useCallback, useState } from 'react';
import { container } from '@/di/container';
import type { Visita } from '@/domain/entities/visitas';
import type { CreateVisitaDTO } from '@/application/dto/visitas/CreateVisitaDTO';
import type { UpdateVisitaDTO } from '@/application/dto/visitas/UpdateVisita';
import type { GetVisitasUseCase } from '@/application/use-cases/visita/GetVisitasUseCase';
import type { CreateVisitaUseCase } from '@/application/use-cases/visita/CreateVisitaUseCase';
import type { UpdateVisitaFechaUseCase } from '@/application/use-cases/visita/UpdateVisitaFechaUseCase';

export function useVisita() {
  const [visita, setVisita] = useState<Visita | null>(null);
  const [data, setData] = useState<Visita[]>([]);
  const [pending, setPending] = useState(0);
  const [error, setError] = useState<string>();
  const clearError = useCallback(() => setError(undefined), []);

  const runRequest = useCallback(
    async <T>(operation: () => Promise<T>, message: string): Promise<T> => {
      setPending(current => current + 1);
      setError(undefined);
      try {
        return await operation();
      } catch (err) {
        setError(err instanceof Error ? err.message : message);
        throw err;
      } finally {
        setPending(current => current - 1);
      }
    },
    []
  );

  const loadAll = useCallback(async () => {
    const result = await runRequest(
      () => container.get<GetVisitasUseCase>('GetVisitasUseCase').execute(),
      'Error al cargar las visitas'
    );
    setData(result);
    return result;
  }, [runRequest]);

  const create = useCallback(
    async (values: CreateVisitaDTO) => {
      const result = await runRequest(
        () =>
          container
            .get<CreateVisitaUseCase>('CreateVisitaUseCase')
            .execute(values),
        'Error al crear la visita'
      );
      setVisita(result);
      // Consultar loadAll tras crear para obtener los conteos y orden del servidor.
      return result;
    },
    [runRequest]
  );

  const updateFecha = useCallback(
    async (id: string | number, values: UpdateVisitaDTO) => {
      const result = await runRequest(
        () =>
          container
            .get<UpdateVisitaFechaUseCase>('UpdateVisitaFechaUseCase')
            .execute(String(id), values),
        'Error al actualizar la fecha de la visita'
      );
      setVisita(current =>
        current?.id === result.id ? { ...current, ...result } : current
      );
      setData(current =>
        current
          .map(item => (item.id === result.id ? { ...item, ...result } : item))
          .sort((a, b) => b.fecha.localeCompare(a.fecha) || b.id - a.id)
      );
      return result;
    },
    [runRequest]
  );

  return {
    visita,
    data,
    loading: pending > 0,
    error,
    loadAll,
    create,
    updateFecha,
    clearError,
  };
}
